<template>
  <v-menu v-model="menuOpen" :close-on-content-click="false" :nudge-width="320" offset-y transition="slide-y-transition"
    bottom left>
    <template v-slot:activator="{ on, attrs }" v-if="!CURRENT_USER_ROLES.includes('manager') && !CURRENT_USER_ROLES.includes('admin')">
      <v-btn v-bind="attrs" v-on="on" icon class="notification-bell-btn mr-3" @click="handleBellClick">
        <v-badge :value="unreadedNotifications > 0" :content="unreadedNotifications > 9 ? '9+' : unreadedNotifications"
          color="error" overlap>
          <v-icon :class="{ 'bell-animation': unreadedNotifications > 0 }"
            :color="unreadedNotifications > 0 ? 'white' : undefined">
            mdi-bell-outline
          </v-icon>
        </v-badge>
      </v-btn>
    </template>

    <v-card class="notification-panel elevation-5">
      <!-- Header -->
      <v-toolbar flat color="primary" dark dense>
        <v-toolbar-title>
          <v-icon left>mdi-bell-outline</v-icon>
          Notificaciones
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn v-if="notifications.length > 0" text small @click="markAllAsRead">
          <v-icon left x-small>mdi-check-all</v-icon>
          Marcar todo como leído
        </v-btn>
      </v-toolbar>

      <!-- Banner para solicitar permisos de notificación -->
      <v-alert
        v-if="showPermissionAlert"
        type="info"
        dense
        text
        dismissible
        @input="showPermissionAlert = false"
        class="ma-2 mb-0"
      >
        <div class="d-flex align-center">
          <div class="flex-grow-1">
            <strong>¿Activar notificaciones del navegador?</strong>
            <div class="caption">Recibe alertas cuando lleguen nuevas notificaciones</div>
          </div>
          <v-btn
            small
            color="primary"
            @click="requestNotificationPermissionExplicit"
            class="ml-2"
          >
            Activar
          </v-btn>
        </div>
      </v-alert>

      <!-- Banner para permisos bloqueados -->
      <v-alert
        v-if="showPermissionDeniedAlert"
        type="warning"
        dense
        text
        dismissible
        @input="showPermissionDeniedAlert = false"
        class="ma-2 mb-0"
      >
        <div>
          <strong>Notificaciones bloqueadas</strong>
          <div class="caption mt-1">
            Las notificaciones del navegador están bloqueadas. Para activarlas:
          </div>
          <div class="caption mt-1">
            <strong>Chrome/Edge:</strong> Click en el candado 🔒 junto a la URL → Permisos del sitio → Notificaciones → Permitir
          </div>
          <div class="caption">
            <strong>Firefox:</strong> Click en el candado 🔒 → Permisos → Notificaciones → Permitir
          </div>
          <div class="caption">
            <strong>Safari:</strong> Safari → Preferencias → Sitios web → Notificaciones → Permitir para este sitio
          </div>
          <v-btn
            x-small
            outlined
            color="primary"
            class="mt-2"
            @click="recheckPermissions"
          >
            <v-icon left x-small>mdi-refresh</v-icon>
            Verificar de nuevo
          </v-btn>
        </div>
      </v-alert>

      <!-- Banner de éxito con botón de prueba -->
      <!-- <v-alert
        v-if="notificationPermissionGranted && !showPermissionAlert && !showPermissionDeniedAlert"
        type="success"
        dense
        text
        dismissible
        class="ma-2 mb-0"
      >
        <div class="d-flex align-center">
          <div class="flex-grow-1">
            <strong>✓ Notificaciones activadas</strong>
            <div class="caption">Las notificaciones del navegador están habilitadas</div>
          </div>
          <v-btn
            x-small
            outlined
            color="success"
            @click="showTestNotification"
            class="ml-2"
          >
            <v-icon left x-small>mdi-bell-ring</v-icon>
            Probar
          </v-btn>
        </div>
      </v-alert> -->

      <!-- Navigation tabs -->
      <v-tabs v-model="activeTab" background-color="transparent" color="primary" centered grow>
        <v-tab>
          <v-icon small class="mr-1">mdi-bell</v-icon>
          Todas
          <v-badge v-if="notifications.length > 0" color="primary" content-class="primary--text"
            :content="notifications.length" inline small offset-x="3" offset-y="-1"></v-badge>
        </v-tab>
        <v-tab>
          <v-icon small class="mr-1">mdi-car</v-icon>
          Vehículos
          <v-badge v-if="vehicleNotificationsCount > 0" color="primary" content-class="primary--text"
            :content="vehicleNotificationsCount" inline small offset-x="3" offset-y="-1"></v-badge>
        </v-tab>
        <v-tab>
          <v-icon small class="mr-1">mdi-car-arrow-right</v-icon>
          Movimientos
          <v-badge v-if="movementNotificationsCount > 0" color="primary" content-class="primary--text"
            :content="movementNotificationsCount" inline small offset-x="3" offset-y="-1"></v-badge>
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <!-- Loading/Empty states -->
      <v-card-text v-if="loadingNotifications" class="text-center py-5">
        <v-progress-circular size="40" color="primary" indeterminate class="mb-3"></v-progress-circular>
        <div class="caption grey--text text--darken-1">Cargando notificaciones...</div>
      </v-card-text>

      <v-card-text v-else-if="getCurrentTabNotifications.length === 0" class="text-center py-5">
        <v-icon size="64" color="grey lighten-2">mdi-bell-sleep-outline</v-icon>
        <div class="body-2 grey--text text--darken-1 mt-3">
          No hay notificaciones pendientes en esta categoría
        </div>
      </v-card-text>

      <!-- Notification list -->
      <v-tabs-items v-model="activeTab" v-else>
        <v-tab-item v-for="tabIndex in 3" :key="tabIndex - 1">          <v-list two-line dense class="notification-list">            <template v-for="(notification, index) in getCurrentTabNotifications">
              <v-divider v-if="index > 0" :key="`div-${notification.id}`"></v-divider>

              <v-list-item :key="notification.id" @click="handleNotificationClick(notification)"
                :class="{ 'notification-unread': notification.read_at === null }">
                <!-- Notification icon -->
                <v-list-item-avatar class="my-1">
                  <v-avatar size="40" :color="getNotificationColor(notification)" class="white--text elevation-1">
                    <v-icon dark>{{ getNotificationIcon(notification) }}</v-icon>
                  </v-avatar>
                </v-list-item-avatar>

                <!-- Notification content -->
                <v-list-item-content>
                  <v-list-item-title class="body-2">
                    {{ getNotificationTitle(notification) }}
                  </v-list-item-title>

                  <v-list-item-subtitle class="caption d-flex align-center mt-1">
                    <v-icon size="14" class="mr-1 grey--text">mdi-clock-outline</v-icon>
                    {{ formatDate(notification.created_at) }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <!-- Action buttons -->
                <v-list-item-action class="my-0">
                  <v-btn icon x-small @click.stop="markAsRead(notification)" :disabled="notification.read_at !== null"
                    :color="notification.read_at === null ? 'primary' : 'grey'">
                    <v-icon>
                      {{ notification.read_at === null ? 'mdi-email-outline' : 'mdi-email-open-outline' }}
                    </v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-tab-item>
      </v-tabs-items>

      <!-- Footer -->
      <v-divider v-if="notifications.length > 0"></v-divider>
      <v-card-actions v-if="notifications.length > 0">
        <v-spacer></v-spacer>
        <v-btn text color="primary" :to="{ name: 'Notificaciones' }" @click="menuOpen = false">
          Ver todas
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import NotificacionService from '../../services/NotificacionService'
import moment from 'moment'
import { mapGetters } from 'vuex'

export default {
  data: () => ({
    menuOpen: false,
    activeTab: 0,
    notifications: [],
    notificationsSolicitud: [],
    unreadedNotifications: 0,
    loadingNotifications: false,
    polling: null,
    previousNotificationIds: new Set(),
    notificationPermissionGranted: false,
    showPermissionAlert: false,
    showPermissionDeniedAlert: false,
  // Original document title to restore when clearing badge
  originalTitle: (typeof document !== 'undefined' ? document.title : ''),
  // Internal flag to know if we currently applied a tab badge
  tabBadgeActive: false,
  titleBlinkInterval: null,
  isLoadingNotificationsInProgress: false
  }),

  computed: {
    ...mapGetters(['CURRENT_USER_ROLES', 'CURRENT_USER']),
    getCurrentTabNotifications () {
      if (this.activeTab === 0) {
        // Mostrar todas las notificaciones
        return this.notifications
      } else if (this.activeTab === 1) {
        // Vehículos: incluir todos los tipos relacionados con vehículos
        const vehicleNotifications = this.notifications.filter(n => {
          const vehicleTypes = [
            'vehicle',
            'damage_movement',
            'damage_first_reception_used',
            'damage_first_reception_new',
            'equipment_missing_chalecos',
            'equipment_missing_baliza_v16',
            'equipment_missing_documentacion',
            'equipment_missing_triangulo',
            'equipment_missing_cable_carga'
          ]
          return vehicleTypes.includes(n.type) && n.user_id === this.CURRENT_USER.id
        })

        // Eliminar duplicados
        const seen = new Map()
        const deduped = []
        vehicleNotifications.forEach(n => {
          const key = [
            n.type || '',
            n.vehicle?.id || '',
            n.movement?.id || ''
          ].join('|')
          if (!seen.has(key)) {
            seen.set(key, true)
            deduped.push(n)
          }
        })

        return deduped
      } else if (this.activeTab === 2) {
        // Movimientos: filtrar por tipo movement y tipos relacionados con movimientos
        return this.notifications.filter(n =>
          (n.type === 'movement' || n.type === 'movement_datetime_changed') && n.user_id === this.CURRENT_USER.id
        )
      }
      return []
    },

    // Contadores para las pestañas
    allNotificationsCount() {
      return this.notifications.length
    },

    vehicleNotificationsCount() {
      const vehicleNotifications = this.notifications.filter(n => {
        const vehicleTypes = [
          'vehicle',
          'damage_movement',
          'damage_first_reception_used',
          'damage_first_reception_new',
          'equipment_missing_chalecos',
          'equipment_missing_baliza_v16',
          'equipment_missing_documentacion',
          'equipment_missing_triangulo',
          'equipment_missing_cable_carga'
        ]
        return vehicleTypes.includes(n.type) && n.user_id === this.CURRENT_USER.id
      })

      // Eliminar duplicados para el conteo
      const seen = new Map()
      vehicleNotifications.forEach(n => {
        const key = [
          n.type || '',
          n.vehicle?.id || '',
          n.movement?.id || ''
        ].join('|')
        seen.set(key, true)
      })

      return seen.size
    },

    movementNotificationsCount() {
      return this.notifications.filter(n =>
        (n.type === 'movement' || n.type === 'movement_datetime_changed') && n.user_id === this.CURRENT_USER.id
      ).length
    }
  },

  watch: {
    menuOpen (newVal) {
      if (newVal) {
        this.clearTabBadge()
      }
    }
  },

  created () {
    // this.showTestNotification()

    // Cargar previousNotificationIds desde localStorage
    try {
      const savedIds = localStorage.getItem('previousNotificationIds')
      if (savedIds) {
        this.previousNotificationIds = new Set(JSON.parse(savedIds))
      }
    } catch (e) {
      console.warn('Error cargando previousNotificationIds desde localStorage:', e)
    }

    this.requestNotificationPermission()
    this.loadNotifications()
    this.polling = setInterval(this.loadNotifications, 30000)

    // Keep track of original title and listen to visibility/focus to clear badge
    try {
      if (typeof document !== 'undefined') {
        // Clean ANY prefixes from the title (emojis, numbers, badges, etc)
        // The title format from router is: "Page Name | Pivot Fleet"
        let currentTitle = document.title
        // Remove any emoji at the start
        currentTitle = currentTitle.replace(/^[\u{1F534}\u{26AA}\u{1F300}-\u{1F9FF}]\s*/u, '')
        // Remove any (number) pattern
        currentTitle = currentTitle.replace(/^\(\d+\)\s*/, '')
        // Remove any number: pattern
        currentTitle = currentTitle.replace(/^\d+:\s*/, '')
        // Store the clean title
        this.originalTitle = currentTitle
      }
      window.addEventListener('focus', this.clearTabBadge)
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
    } catch (e) {
      console.warn('Error setting up tab badge listeners', e)
    }
  },

  methods: {
    async requestNotificationPermission() {
      // Verificar si el navegador soporta notificaciones
      if (!('Notification' in window)) {
        console.warn('❌ El navegador no soporta notificaciones')
        return
      }

      // Si ya tenemos permiso, marcarlo
      if (Notification.permission === 'granted') {
        this.notificationPermissionGranted = true
        this.showPermissionAlert = false
        this.showPermissionDeniedAlert = false
        return
      }

      // Si está denegado, mostrar el banner de ayuda
      if (Notification.permission === 'denied') {
        this.showPermissionAlert = false
        this.showPermissionDeniedAlert = true
        return
      }

      // Si no ha respondido, mostrar el alert para solicitar
      if (Notification.permission === 'default') {
        this.showPermissionAlert = true
        this.showPermissionDeniedAlert = false
      }
    },

    async requestNotificationPermissionExplicit() {
      // Verificar si el navegador soporta notificaciones
      if (!('Notification' in window)) {
        this.$toast.error('Tu navegador no soporta notificaciones de escritorio')
        return
      }

      try {
        const permission = await Notification.requestPermission()
        
        if (permission === 'granted') {
          this.notificationPermissionGranted = true
          this.showPermissionAlert = false
          this.$toast.success('Notificaciones del navegador activadas')
          
          // Mostrar una notificación de prueba
          this.showTestNotification()
        } else if (permission === 'denied') {
          this.showPermissionAlert = false
          this.$toast.warning('Has denegado los permisos de notificación. Puedes activarlos desde la configuración del navegador.')
        }
      } catch (error) {
        console.error('Error al solicitar permisos de notificación:', error)
        this.$toast.error('Error al solicitar permisos de notificación')
      }
    },

    showTestNotification() {
      if (Notification.permission !== 'granted') {
        console.error('No se puede mostrar la notificación: permisos no concedidos')
        return
      }

      try {
        const notification = new Notification('¡Notificaciones activadas!', {
          body: 'Ahora recibirás notificaciones cuando lleguen nuevas alertas',
          icon: '/noti_icon.png',
          badge: '/noti_icon.png',
          tag: 'test-notification',
          requireInteraction: false
        })

        notification.onclick = () => {
          window.focus()
          notification.close()
        }

        notification.onerror = (error) => {
          console.error('Error en la notificación:', error)
        }

        setTimeout(() => {
          notification.close()
        }, 5000)
      } catch (error) {
        console.error('Error al crear la notificación:', error)
      }
    },

    recheckPermissions() {
      this.showPermissionDeniedAlert = false
      this.requestNotificationPermission()
    },

    showBrowserNotification(notification) {
      // Verificar si tenemos permiso
      if (!this.notificationPermissionGranted || Notification.permission !== 'granted') {
        return
      }

      try {
        const title = `Nueva notificación - ${this.formatDate(notification.created_at)}`
        const options = {
          body: this.getNotificationTitle(notification),
          icon: '/noti_icon.png',
          badge: '/noti_icon.png',
          tag: `notification-${notification.id}`, // Para evitar duplicados
          requireInteraction: false,
          silent: false
        }
        // Crear la notificación
        const browserNotification = new Notification(title, options)

        // Manejar el click en la notificación
        browserNotification.onclick = () => {
          window.focus()
          this.handleNotificationClick(notification)
          browserNotification.close()
        }

        browserNotification.onerror = (error) => {
          console.error('❌ Error en la notificación del navegador:', error)
        }
      } catch (error) {
        console.error('❌ Error al crear notificación del navegador:', error)
      }
    },
    getNotificationsByType (type) {
      if (type === 'vehicle') {
        // Incluir solo vehicle y tipos de daño relacionados (los equipment_missing se manejan por separado en getCurrentTabNotifications)
        const list = this.notifications.filter(n =>
          n.type === 'vehicle' ||
          n.type === 'damage_first_reception_used' ||
          n.type === 'damage_first_reception_new'
        )

        // Mantener solo notificaciones del usuario actual
        const userNotifications = list.filter(n => n.user_id === this.CURRENT_USER.id)

        // Eliminar duplicados usando la misma key que en loadNotifications
        const seen = new Map()
        const deduped = []
        userNotifications.forEach(n => {
          const key = [
            n.type || '',
            n.vehicle?.id || '',
            n.movement?.id || ''
          ].join('|')
          if (!seen.has(key)) {
            seen.set(key, true)
            deduped.push(n)
          }
        })

        return deduped
      }

      // Para otros tipos, filtrar por tipo y por usuario
      return this.notifications
        .filter(n => n.type === type)
        .filter(n => n.user_id === this.CURRENT_USER.id)
    },

    handleBellClick () {
      if (this.menuOpen) {
        this.loadNotifications()
      }
      // If opening the menu, clear the badge since user is checking
      if (this.menuOpen) {
        this.clearTabBadge()
      }
    },

    // Update the browser/tab badge or document title with unread count
    async updateTabBadge () {
      try {
        // If page is visible and user is on the tab, don't badge
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          await this.clearTabBadge()
          return
        }

        const count = this.unreadedNotifications || 0
        
        if (count > 0) {
          // Visual effects for better attention
          if (typeof document !== 'undefined') {
            // Stop any existing blink
            if (this.titleBlinkInterval) {
              clearInterval(this.titleBlinkInterval)
            }
            
            // ALWAYS clean from current document.title to avoid accumulation
            let currentTitle = document.title
            // Remove ALL possible prefixes aggressively
            // First remove all (number) patterns - can be multiple like (9)(9)(9)
            currentTitle = currentTitle.replace(/(\(\d+\))+/g, '')
            // Remove all emoji at the start
            currentTitle = currentTitle.replace(/^[🔴⚪]+\s*/g, '')
            // Remove number: pattern
            currentTitle = currentTitle.replace(/^\d+:\s*/, '')
            // Trim any extra spaces
            currentTitle = currentTitle.trim()
            
            // Create a blinking title effect with ONLY emoji (NO numbers anywhere)
            let showAlert = true
            this.titleBlinkInterval = setInterval(() => {
              if (showAlert) {
                document.title = `🔴 ${currentTitle}`
              } else {
                document.title = `⚪ ${currentTitle}`
              }
              showAlert = !showAlert
            }, 100) // Blink every second
          }
          
          this.tabBadgeActive = true
        } else {
          await this.clearTabBadge()
        }
      } catch (err) {
        console.warn('Error updating tab badge:', err)
      }
    },

    // Clear any tab badge applied earlier
    async clearTabBadge () {
      // Clear blinking title interval
      if (this.titleBlinkInterval) {
        clearInterval(this.titleBlinkInterval)
        this.titleBlinkInterval = null
      }
      
      if (typeof document !== 'undefined') {
        // Clean from current title to remove any accumulated badges
        let cleanTitle = document.title
        // Remove all (number) patterns - can be multiple like (9)(9)(9)
        cleanTitle = cleanTitle.replace(/(\(\d+\))+/g, '')
        // Remove all emoji
        cleanTitle = cleanTitle.replace(/^[🔴⚪]+\s*/g, '')
        // Remove number: pattern
        cleanTitle = cleanTitle.replace(/^\d+:\s*/, '')
        // Trim spaces
        cleanTitle = cleanTitle.trim()
        
        document.title = cleanTitle
        this.tabBadgeActive = false
      }
    },

    // When visibility changes, clear badge if user returns
    handleVisibilityChange () {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        this.clearTabBadge()
      }
    },

    async handleNotificationClick (notification) {
      // Mark as read when clicked
      if (notification.read_at === null) {
        await this.markAsRead(notification)
      }

      // Reload notifications to keep list updated
      await this.loadNotifications()

      // Navigate based on notification type
      this.menuOpen = false

      if (notification.vehicle && notification.vehicle.status === 'pending' && !notification.movement) {
        // Verificar si ya estamos en la página de detalle del vehículo
        if (this.$route.name !== 'VehiculoDetail' || this.$route.params.id !== notification.vehicle.id.toString()) {
          await this.$router.push({ name: 'VehiculoDetail', params: { id: notification.vehicle.id } })
        }
        return
      }

      if (notification.movement) {
        // Verificar si ya estamos en la página de detalle del movimiento
        if (this.$route.name !== 'MovimientoDetail' || this.$route.params.movimiento_id !== notification.movement.id.toString()) {
          await this.$router.push({ name: 'MovimientoDetail', params: { movimiento_id: notification.movement.id } })
        }
      }
    },

    async markAsRead (notification) {
      try {
        await NotificacionService.read(notification)

        // Update local state
        const index = this.notifications.findIndex(n => n.id === notification.id)
        if (index !== -1) {
          this.$set(this.notifications[index], 'read_at', new Date().toISOString())
          this.calculateUnreadCount()
        }

        // Reload notifications to keep list updated
        await this.loadNotifications()
      } catch (error) {
        console.error('Error marking notification as read:', error)
      }
    },

    async markAllAsRead () {
      try {
        await NotificacionService.readAll()

        // Update local state
        this.notifications.forEach(notification => {
          this.$set(notification, 'read_at', new Date().toISOString())
        })

        // Limpiar el Set de IDs previos cuando se marcan todas como leídas
        this.previousNotificationIds.clear()
        try {
          localStorage.removeItem('previousNotificationIds')
        } catch (e) {
          console.warn('Error limpiando previousNotificationIds de localStorage:', e)
        }

        this.calculateUnreadCount()
        this.loadNotifications()
      } catch (error) {
        console.error('Error marking all notifications as read:', error)
      }
    },

    calculateUnreadCount () {
      this.unreadedNotifications = this.notifications.length
  // Update tab badge according to unread count and visibility
  this.updateTabBadge()
    },

    async loadNotifications () {
      // Guard para evitar múltiples ejecuciones simultáneas
      if (this.isLoadingNotificationsInProgress) {
        return
      }
      this.isLoadingNotificationsInProgress = true
      this.loadingNotifications = true
      try {
        const params = {
          'filter[readStatus]': 0
        }
      const response = await NotificacionService.get(params)
      
      // Aplicamos el filtro para incluir todas las notificaciones relevantes
      // Las no leídas siempre se mostrarán, las leídas solo se guardan para
      // referencia pero no se mostrarán en la UI
      const filteredNotifications = response.data.data.filter(notification =>
        (notification.vehicle && notification.vehicle.status === 'pending') ||
        notification.movement
      )
      
      // Mantener solo notificaciones del usuario actual y eliminar duplicados
      const userNotifications = filteredNotifications.filter(n => n.user_id === this.CURRENT_USER.id)

      const seen = new Map()
      const deduped = []
      userNotifications.forEach(n => {
        const key = [
          n.type || '',
          n.vehicle?.id || '',
          n.movement?.id || ''
        ].join('|')
        if (!seen.has(key)) {
          seen.set(key, true)
          deduped.push(n)
        }
      })

      // Ordenar por fecha (más reciente primero)
      deduped.sort((a, b) => (b.created_at || 0) - (a.created_at || 0))
      // Detectar nuevas notificaciones (comparar ANTES de actualizar el Set)
      const newNotifications = deduped.filter(n => !this.previousNotificationIds.has(n.id))
      // Actualizar el conjunto de IDs de notificaciones ANTES de mostrar notificaciones
      this.previousNotificationIds = new Set(deduped.map(n => n.id))

      // Guardar en localStorage para persistir entre remontajes del componente
      try {
        localStorage.setItem('previousNotificationIds', JSON.stringify(Array.from(this.previousNotificationIds)))
      } catch (e) {
        console.warn('Error guardando previousNotificationIds en localStorage:', e)
      }
      // Mostrar notificaciones del navegador SIEMPRE que haya nuevas notificaciones
      if (newNotifications.length > 0) {
        // Mostrar notificación del navegador para cada nueva notificación
        newNotifications.forEach(notification => {
          this.showBrowserNotification(notification)
        })
      }

      this.notifications = deduped
      this.calculateUnreadCount()
      } catch (error) {
      console.error('Error loading notifications:', error)
      } finally {
      this.loadingNotifications = false
      this.isLoadingNotificationsInProgress = false
      }
    },

    getNotificationIcon (notification) {
      if (
        notification.type === 'vehicle' ||
        notification.type === 'damage_movement' ||
        notification.type === 'damage_first_reception_used' ||
        notification.type === 'damage_first_reception_new' ||
        notification.type === 'equipment_missing_chalecos' ||
        notification.type === 'equipment_missing_baliza_v16' ||
        notification.type === 'equipment_missing_documentacion' ||
        notification.type === 'equipment_missing_triangulo' ||
        notification.type === 'equipment_missing_cable_carga'
      ) {
        return 'mdi-car'
      }
      if (notification.type === 'movement' || notification.type === 'movement_datetime_changed') {
        return 'mdi-car-arrow-right'
      }
      return 'mdi-bell-alert'
    },
    formatDate (timestamp) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp * 1000)
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date)
    },

    getNotificationColor (notification) {
      if (notification.read_at === null) {
        if (notification.type === 'vehicle' || notification.type === 'damage_movement' || notification.type === 'damage_first_reception_used' || notification.type === 'damage_first_reception_new' ||
        notification.type === 'equipment_missing_chalecos' ||
        notification.type === 'equipment_missing_baliza_v16' ||
        notification.type === 'equipment_missing_documentacion' ||
        notification.type === 'equipment_missing_triangulo' ||
        notification.type === 'equipment_missing_cable_carga') {
          return 'indigo'
        }
        if (notification.type === 'movement' || notification.type === 'movement_datetime_changed') {
          return 'deep-purple'
        }
        return 'red darken-1'
      }
      return 'grey'
    },

    getNotificationTitle (notification) {
      if (notification.vehicle && notification.vehicle.status === 'pending' && notification.type === 'vehicle') {
        return `Vehículo pendiente: ${notification.vehicle.brand} ${notification.vehicle.model || ''}`
      }
      if (notification.type === 'damage_first_reception_used') {
        return `Vehículo usado dañado 1ra recepción: ${notification.vehicle?.brand || ''} ${notification.vehicle?.model || ''}`
      }
      if (notification.type === 'damage_first_reception_new') {
        return `Vehículo nuevo dañado 1ra recepción: ${notification.vehicle?.brand || ''} ${notification.vehicle?.model || ''}`
      }
      if (notification.type === 'damage_movement') {
        return `Vehículo dañado: ${notification.vehicle?.brand || ''} ${notification.vehicle?.model || ''}`
      }
      if (notification.type && notification.type.startsWith('equipment_missing_')) {
        // Mostrar el nombre del equipamiento faltante
        const equip = notification.type.replace('equipment_missing_', '')
        const equipMap = {
          'baliza_v16': 'Baliza V16',
          'chalecos': 'Chaleco reflectante',
          'triangulo': 'Triángulo de emergencia',
          'documentacion': 'Documentación',
          'cable_carga': 'Cable de carga'
        }
        const equipLabel = equipMap[equip] || equip
        return `Falta equipamiento: ${equipLabel} (${notification.vehicle?.plate_number || ''})`
      }
      if (notification.type === 'movement') {
        let vehicleInfo = notification.vehicle
          ? `(${notification.vehicle.brand} ${notification.vehicle.model || ''})`
          : ''
        let statusText = 'pendiente'
        if (notification.movement && notification.movement.status) {
          if (notification.movement.status === 'unreported') {
            statusText = 'no reportado'
          }
        }
        return `Movimiento ${statusText} ${vehicleInfo}`
      }
      if (notification.type === 'movement_datetime_changed') {
        // Usar el título que viene del backend si está disponible
        if (notification.title) {
          return notification.title
        }
        // Fallback si no hay título
        let vehicleInfo = notification.vehicle?.plate_number || 
                         `${notification.vehicle?.brand || ''} ${notification.vehicle?.model || ''}`.trim()
        return `Fecha de movimiento modificada${vehicleInfo ? ' - ' + vehicleInfo : ''}`
      }
      return 'Nueva notificación'
    },

    formatTimeAgo (timestamp) {
      if (!timestamp) return ''

      const now = moment()
      const notificationTime = moment(timestamp)
      const diff = now.diff(notificationTime, 'days')

      if (diff === 0) {
        return `Hoy, ${notificationTime.format('HH:mm')}`
      } else if (diff === 1) {
        return `Ayer, ${notificationTime.format('HH:mm')}`
      } else if (diff < 7) {
        return notificationTime.format('dddd, HH:mm')
      } else {
        return notificationTime.format('DD/MM/YYYY')
      }
    }
  },

  beforeDestroy () {
    clearInterval(this.polling)
    
    // Clear title blink interval
    if (this.titleBlinkInterval) {
      clearInterval(this.titleBlinkInterval)
    }
    
    try {
      window.removeEventListener('focus', this.clearTabBadge)
      document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    } catch (e) {
      // ignore
    }
  }
}
</script>

<style lang="scss" scoped>
.notification-panel {
  max-height: 70vh;
  overflow-y: auto;
  border-radius: 8px;
}

.notification-list {
  max-height: 350px;
  overflow-y: auto;
}

.notification-unread {
  background-color: rgba(33, 150, 243, 0.05);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: var(--v-primary-base);
  }
}

.bell-animation {
  animation: bellRing 2s infinite;
  transform-origin: 50% 0%;
}

@keyframes bellRing {
  0% {
    transform: rotate(0);
  }

  1% {
    transform: rotate(15deg);
  }

  3% {
    transform: rotate(-13deg);
  }

  5% {
    transform: rotate(13deg);
  }

  7% {
    transform: rotate(-8deg);
  }

  9% {
    transform: rotate(8deg);
  }

  11% {
    transform: rotate(0);
  }

  100% {
    transform: rotate(0);
  }
}

// Mejoras para las tarjetas en los tabs
.v-tabs-items {
  background-color: transparent;
}

.v-tab {
  min-width: 0;
  text-transform: none;
  letter-spacing: 0;
}

// Estilos para el badge
::v-deep .v-badge__badge {
  padding: 4px;
  height: auto;
  min-width: 20px;
}
</style>
